import { convertDateToString     ,
         convertStringToDate
} from '../timeManagment/utils';


import {
  startOfMonth,
  endOfMonth,
  addMinutes,
  addHours,
  addDays,
  addMonths,
  addYears
} from 'date-fns';


export interface GroupedData<T> {
  groups: Map<string, T[]>;
  renderer?: (val: string) => string;
}

export const initializedGroupedData = <T>(): GroupedData<T> => {
  return {
    groups: new Map<string, T[]>(),
  };
};


export interface GroupByResult {
  key: string;
  renderer?: (val: string) => string;
}

export const groupData = <T> ( data    : T[]                        ,
                               groupBy : (item: T) => GroupByResult ): GroupedData<T> => {


  if (!Array.isArray(data)) 
   {
    return { groups: new Map<string, T[]>(), renderer: undefined };
   }

  const groups = new Map<string, T[]>();
  
  let renderer: ((val: string) => string) | undefined = undefined;

  data.forEach((item) => {
    const { key, renderer: itemRenderer } = groupBy(item);

    if (!groups.has(key)) {
      groups.set(key, []);
    }

    groups.get(key)!.push(item);

    // Si un renderer est fourni et qu'il n'est pas encore défini, l'assigner
    if (itemRenderer && !renderer) {
      renderer = itemRenderer;
    }
  });

  // Trier les groupes par clé (utile si la clé est une date formatée en "yyyy/MM/dd HH:mm")
  const sortedGroups = new Map(Array.from(groups.entries()).sort(([keyA], [keyB]) => keyA.localeCompare(keyB)));

  return { groups: sortedGroups, renderer };
};

export const completeGroupData = <T> ( groupedData   : GroupedData<T> ,
                                       timeStep      : string         ,
                                       startingDate? : Date           ,
                                       endingDate?   : Date           ,
							           keyType       : string         = 'date' ): GroupedData<T> => {
  
 
        if ( keyType === 'date'   ) return completeGroupDataForDateOrderSort         ( groupedData , timeStep , startingDate , endingDate )
   else if ( keyType === 'letter' ) return completeGroupDataForAlphabeticalOrderSort ( groupedData )
   else                             return groupedData
  }													     


export const completeGroupDataForDateOrderSort = <T> ( groupedData   : GroupedData<T> ,
                                                       timeStep      : string         ,
                                                       startingDate? : Date           ,
                                                       endingDate?   : Date           ): GroupedData<T> => {


  const completedGroups = new Map(groupedData.groups);
  const existingKeys = new Set(completedGroups.keys());

  if (existingKeys.size === 0) return groupedData;

  const allDates = Array.from(existingKeys)
    .map((key) => convertStringToDate(key))
    .filter((d): d is Date => d !== null);

  let minDate, maxDate;

       if ( startingDate && endingDate )
        {
         minDate = startingDate;
         maxDate = endingDate;
        }

  else if (timeStep === "1 jour")
        {
         minDate = new Date ( Math.min (...allDates.map ( (d) => d.getTime() ) ) );
         maxDate = new Date ( Math.max (...allDates.map ( (d) => d.getTime() ) ) );
        }

  else if (timeStep === "1 mois")
        {
         const months = allDates.map((date) => ({ year: date.getFullYear() ,
                                                  month: date.getMonth()   }));

         const minMonth = months.reduce ( (prev, curr) => prev.year < curr.year || (prev.year === curr.year && prev.month < curr.month) ? prev : curr );
         const maxMonth = months.reduce ( (prev, curr) => prev.year > curr.year || (prev.year === curr.year && prev.month > curr.month) ? prev : curr );

         minDate = startOfMonth ( new Date (minMonth.year, minMonth.month) );
         maxDate = endOfMonth   ( new Date (maxMonth.year, maxMonth.month) );
        }

  else  {
         minDate = new Date ( Math.min (...allDates.map ( (d) => d.getTime() ) ) );
         maxDate = new Date ( Math.max (...allDates.map ( (d) => d.getTime() ) ) );
        }

  const existingRenderer = groupedData.renderer;

  let currentDate = new Date(minDate);
  
  while (currentDate <= maxDate) 
   {
    const key = convertDateToString(currentDate, getDateFormat(timeStep));

    if (!existingKeys.has(key)) 
	 {
      completedGroups.set(key, []);
     }

 //   currentDate = timeStep === "1 jour" ? addDays(currentDate, 1) : addMonths(currentDate, 1);


    switch ( timeStep )
	 {
      case "1 minute"   : currentDate = addMinutes ( currentDate ,  1); break;
      case "5 minutes"  : currentDate = addMinutes ( currentDate ,  5); break;
      case "10 minutes" : currentDate = addMinutes ( currentDate , 10); break;
      case "1 heure"    : currentDate = addHours   ( currentDate ,  1); break;
      case "6 heures"   : currentDate = addHours   ( currentDate ,  6); break;
      case "12 heures"  : currentDate = addHours   ( currentDate , 12); break;
      case "1 jour"     : currentDate = addDays    ( currentDate ,  1); break;
      case "1 semaine"  : currentDate = addDays    ( currentDate ,  7); break;
      case "2 semaines" : currentDate = addDays    ( currentDate , 14); break;
      case "1 mois"     : currentDate = addMonths  ( currentDate ,  1); break;
      case "6 mois"     : currentDate = addMonths  ( currentDate ,  6); break;
      case "1 an"       : currentDate = addYears   ( currentDate ,  1); break;
      default           : throw new Error(`Unsupported timeStep: ${timeStep}`);
	 }
   }

const sortedGroups = new Map(
  Array.from(completedGroups.entries()).sort(([keyA], [keyB]) =>
    keyA.localeCompare(keyB)
  )
);

  return { groups: sortedGroups, renderer: existingRenderer };
};

//////////////////////////////

export const completeGroupDataForAlphabeticalOrderSort = <T>( groupedData: GroupedData<T> ): GroupedData<T> => {
	
	
  const completedGroups = new Map(groupedData.groups);
  const existingKeys = new Set(completedGroups.keys());

  if (existingKeys.size === 0) return groupedData;

  const existingRenderer = groupedData.renderer;

  for (let i = 65; i <= 90; i++) 
   {
    const letter = String.fromCharCode(i);
	
    if (!existingKeys.has(letter)) 
	 {
      completedGroups.set(letter, []);
     }
   }

  const sortedGroups = new Map( Array.from(completedGroups.entries()).sort(([keyA], [keyB]) => keyA.localeCompare(keyB) ) );

  return { groups: sortedGroups, renderer: existingRenderer };
};

//////////////////////////////

const getDateFormat = (timeStep: string): string => {
  switch (timeStep) {
    case "1 minute":
    case "5 minutes":
    case "10 minutes": return "yyyy/MM/dd HH:mm";
    case "1 heure":
    case "6 heures":
    case "12 heures": return "yyyy/MM/dd HH";
    case "1 jour":
    case "1 semaine":
    case "2 semaines": return "yyyy/MM/dd";
    case "1 mois":
    case "6 mois": return "yyyy/MM";
    case "1 an": return "yyyy";
    default: throw new Error(`Unsupported timeStep: ${timeStep}`);
  }
};

export interface GeneratedStats<T> {
  stats: Record<string, number>;
  groups: Map<string, T[]>;
  renderer?: (val: string) => string;
}

export const generateStats = <T>(
  groupedData: GroupedData<T>,
  valueMapper: (item: T) => number,
  aggregator: (a: number, b: number) => number
): GeneratedStats<T> => {
  const stats: Record<string, number> = {};

  groupedData.groups.forEach((items, groupKey) => {
    stats[groupKey] = items
      .map(valueMapper)
      .reduce((acc: number, curr: number) => aggregator(acc, curr), 0);
  });

  return {
    stats,
    groups: groupedData.groups, // Utiliser groups au lieu de groupedData
    renderer: groupedData.renderer,
  };
};

export interface ChartData {
  group: string;
  series: { name: string; value: number }[];
}

export const generateChartData = <T>(
  statsResult: GeneratedStats<T>,
  groupedData: GroupedData<T>
): ChartData[] => {
  return Array.from(statsResult.groups.entries()).map(([group, rows]) => {
    // Utiliser le renderer s'il est défini et que rows contient des éléments, sinon utiliser group
    const groupLabel = groupedData.renderer
      ? groupedData.renderer(group)
      : group;

    // Créer une seule série avec un nom par défaut, par exemple "Value"
    const series = [{
      name: "Value", // Vous pouvez personnaliser ce nom selon vos besoins
      value: statsResult.stats[group],
    }];

    return {
      group: groupLabel,
      series: series,
    };
  });
};
